// ── 미팅지역 지도 패널 (Leaflet 기반) ─────────────────────────────────────────
// 2026-06-07: UnifiedCard wrapper 적용 — 다른 큰 카드와 시각 톤 통일.
import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Map as MapIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { isTomorrow, nameColor, formatRegion } from './utils';
import { toDateString } from '../../../utils/date';
import { makeUniqueLabels } from './KoreaMap';
import { UnifiedCard } from './UnifiedCard';
import { accentText, accentBorder } from './widgetAccent';

/** 한국 영역 위경도 범위 */
const KR_LAT = [33.0, 39.3];
const KR_LNG = [124.0, 132.0];

/** 유효한 한국 내 좌표인지 확인 */
function isValidCoord(lat, lng) {
  return (
    Number.isFinite(lat) && Number.isFinite(lng) &&
    lat >= KR_LAT[0] && lat <= KR_LAT[1] &&
    lng >= KR_LNG[0] && lng <= KR_LNG[1]
  );
}

/**
 * 날짜 기준으로 마케터별 방문 좌표의 중심점 계산
 * @returns {Array} { name, lat, lng, count }[]
 */
function computeMarkerCentroids(tmCalls, dateStr) {
  const acc = {};
  (tmCalls || []).forEach((r) => {
    if (toDateString(r.visit_date || '') !== dateStr) return;
    const sp = String(r.sales_person_name || '').trim();
    if (!sp || sp === '-') return;
    const lat = Number(r.latitude), lng = Number(r.longitude);
    if (!isValidCoord(lat, lng)) return;
    if (!acc[sp]) acc[sp] = { latSum: 0, lngSum: 0, count: 0 };
    acc[sp].latSum += lat;
    acc[sp].lngSum += lng;
    acc[sp].count  += 1;
  });
  return Object.entries(acc).map(([name, { latSum, lngSum, count }]) => ({
    name, lat: latSum / count, lng: lngSum / count, count,
  }));
}

/**
 * 오늘/내일 방문 Leaflet 지도 패널
 * Props: meetings (오늘), tomorrowMeetings (내일), tmCalls, today, tomorrow,
 *        accentOverride / sortableId (UnifiedCard 통일 props)
 */
// points(2026-10-24): 위젯 메뉴가 없는 손님 테넌트 직원 — 서버가 계산한 마케터별 평균 점({today, tomorrow}). 없으면 tmCalls 로 계산(종전).
export function TodayMeetingMapPanel({ meetings, tomorrowMeetings, tmCalls, points, today, tomorrow, accentOverride, sortableId, onClick }) {
  const mapContainerRef = useRef(null);
  const mapRef          = useRef(null);
  const layerRef        = useRef(null);

  const todayPoints    = useMemo(() => points?.today || computeMarkerCentroids(tmCalls, today),       [points, tmCalls, today]);
  const tomorrowPoints = useMemo(() => points?.tomorrow || computeMarkerCentroids(tmCalls, tomorrow), [points, tmCalls, tomorrow]);
  const allNames = useMemo(() => [
    ...new Set([...todayPoints.map(m => m.name), ...tomorrowPoints.map(m => m.name)])
  ], [todayPoints, tomorrowPoints]);
  const labels = useMemo(() => makeUniqueLabels(allNames), [allNames]);

  const todayTotal    = (meetings || []).reduce((s, m) => s + m.count, 0);
  const tomorrowTotal = (tomorrowMeetings || []).reduce((s, m) => s + m.count, 0);

  // 마케터별 오늘/내일 요약 정보 (하단 범례용)
  const marketerInfo = useMemo(() => {
    const acc = {};
    const fill = (list, key) => (list || []).forEach(m => {
      const n = m.marketer || m.name;
      if (!n || n === '-') return;
      if (!acc[n]) acc[n] = { todayRegions: {}, todayCount: 0, tomorrowRegions: {}, tomorrowCount: 0 };
      const r = formatRegion(m.region) || '-';
      acc[n][key + 'Regions'][r] = (acc[n][key + 'Regions'][r] || 0) + m.count;
      acc[n][key + 'Count'] += m.count;
    });
    fill(meetings, 'today');
    fill(tomorrowMeetings, 'tomorrow');
    const result = {};
    Object.entries(acc).forEach(([name, d]) => {
      const topT  = Object.entries(d.todayRegions).sort(([,a],[,b]) => b-a)[0];
      const topTm = Object.entries(d.tomorrowRegions).sort(([,a],[,b]) => b-a)[0];
      result[name] = {
        todayCount:    d.todayCount,
        todayRegion:   topT?.[0]  || null,
        tomorrowCount: d.tomorrowCount,
        tomorrowRegion: topTm?.[0] || null,
      };
    });
    return result;
  }, [meetings, tomorrowMeetings]);

  // Leaflet 지도 초기화 (최초 마운트 시 1회)
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = L.map(mapContainerRef.current, { zoomControl: false, preferCanvas: true, attributionControl: false }).setView([36.5, 127.8], 7);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    const layer = L.layerGroup().addTo(map);
    mapRef.current  = map;
    layerRef.current = layer;
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, []);

  // 포인트 변경 시 마커 갱신
  useEffect(() => {
    const map   = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const allPoints = [...todayPoints, ...tomorrowPoints];
    if (allPoints.length === 0) return;
    map.fitBounds(L.latLngBounds(allPoints.map(p => [p.lat, p.lng])), { padding: [20, 20], maxZoom: 7, animate: false });

    // 레이블 겹침 방지 (픽셀 좌표 기반) — 7px 마커 size 에 맞춤
    const badgeW = (name) => name.length * 7 + 8;
    const BADGE_H = 13, GAP = 2;
    const pts = [
      ...todayPoints.map(p    => { const px = map.latLngToLayerPoint([p.lat, p.lng]); return { ...p, day: 'today',    px: px.x, py: px.y, bw: badgeW(p.name) }; }),
      ...tomorrowPoints.map(p => { const px = map.latLngToLayerPoint([p.lat, p.lng]); return { ...p, day: 'tomorrow', px: px.x, py: px.y, bw: badgeW(p.name) }; }),
    ];
    for (let iter = 0; iter < 150; iter++) {
      let moved = false;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i], b = pts[j];
          const needX = (a.bw + b.bw) / 2 + GAP, needY = BADGE_H + GAP;
          const dx = b.px - a.px, dy = b.py - a.py;
          const overlapX = needX - Math.abs(dx), overlapY = needY - Math.abs(dy);
          if (overlapX > 0 && overlapY > 0) {
            if (overlapX <= overlapY) { const push = overlapX / 2; const dir = dx < 0 ? -1 : 1; a.px -= push * dir; b.px += push * dir; }
            else { const push = overlapY / 2; const dir = dy < 0 ? -1 : 1; a.py -= push * dir; b.py += push * dir; }
            moved = true;
          }
        }
      }
      if (!moved) break;
    }
    const adjTodayMap = {}, adjTomorrowMap = {};
    pts.forEach(p => {
      const ll = map.layerPointToLatLng([p.px, p.py]);
      if (p.day === 'today') adjTodayMap[p.name]    = { ...p, lat: ll.lat, lng: ll.lng };
      else                   adjTomorrowMap[p.name] = { ...p, lat: ll.lat, lng: ll.lng };
    });

    // 마커 아이콘 생성 (내일=흰 배경, 오늘=채색 배경)
    const makeIcon = (name, color, isT) => L.divIcon({
      className: '',
      html: `<div style="display:inline-block;padding:1px 3px;border-radius:6px;background:${isT ? '#fff' : color};border:1px solid ${color};white-space:nowrap;font-size:7px;font-weight:800;color:${isT ? color : '#fff'};box-shadow:0 1px 2px rgba(0,0,0,0.2);box-sizing:border-box;line-height:1.2;transform:translate(-50%,-50%);">${name}</div>`,
      iconSize: [0, 0], iconAnchor: [0, 0],
    });
    Object.values(adjTomorrowMap).forEach(({ name, lat, lng, count }) => {
      L.marker([lat, lng], { icon: makeIcon(name, nameColor(name), true), zIndexOffset: 0 })
        .bindTooltip(`${name} ${isTomorrow(tomorrow) ? '내일' : '다음 영업일'} ${count}건`, { permanent: false, direction: 'top' })
        .addTo(layer);
    });
    Object.values(adjTodayMap).forEach(({ name, lat, lng, count }) => {
      L.marker([lat, lng], { icon: makeIcon(name, nameColor(name), false), zIndexOffset: 100 })
        .bindTooltip(`${name} 오늘 ${count}건`, { permanent: false, direction: 'top' })
        .addTo(layer);
    });
    // labels 변수는 makeUniqueLabels의 결과를 사용하나, Leaflet 아이콘에는 full name 사용
    void labels;
  }, [todayPoints, tomorrowPoints, allNames, labels, tomorrow]);

  // 사용자 명세: today_meeting_map 은 watermark X (지도 가리지 않게)
  const tone = accentOverride || 'blue';
  return (
    <UnifiedCard
      code="today_meeting_map"
      title="미팅지역"
      icon={MapIcon}
      accent="blue"
      accentOverride={accentOverride}
      size="L"
      sortableId={sortableId}
      onClick={onClick}
    >
      {/* 헤더 핵심값: 오늘 N · 내일/다음 영업일 N (text-xl accent) */}
      <div className="flex items-baseline justify-end gap-2 mb-2 flex-wrap">
        <span className="inline-flex items-baseline gap-1">
          <span className={cn('text-xl font-black tabular-nums leading-none', accentText(tone))}>{todayTotal}</span>
          <span className="text-[7px] font-bold text-[var(--text-sub)] uppercase tracking-wider">오늘</span>
        </span>
        <span className="text-[var(--text-muted)] text-xs">·</span>
        <span className="inline-flex items-baseline gap-1">
          <span className="text-xl font-black tabular-nums leading-none text-[var(--text-sub)]">{tomorrowTotal}</span>
          <span className="text-[7px] font-bold text-[var(--text-sub)] uppercase tracking-wider">{isTomorrow(tomorrow) ? '내일' : '다음 영업일'}</span>
        </span>
      </div>
      {/* 구분선 (통일 디자인) */}
      <div className={cn('border-t mx-1 my-1.5', accentBorder(tone))} />
      {/* 범례 */}
      <div className="flex items-center gap-3.5 flex-wrap mb-1">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
          <span className="text-[7px] text-[var(--text-sub)]">오늘</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-full border-2 border-slate-400 bg-white dark:bg-pine-900" />
          <span className="text-[7px] text-[var(--text-sub)]">{isTomorrow(tomorrow) ? '내일' : '다음 영업일'}</span>
        </div>
        <span className="text-[7px] text-[var(--text-muted)]">마커 터치 시 이름 표시</span>
      </div>
      {/* 본문: PC + 모바일 모두 좌(지도) + 우(list) — 사용자 명세 "PC처럼 배치".
          모바일 카드 row 175×2=350px / PC 220×2=440px. 지도/list 둘 다 카드 안에 들어감.
          list = wrap 허용 (... 잘림 X). */}
      <div className="flex flex-row gap-2 flex-1 min-h-0">
        {/* 지도 — 너비 더 키움 + 높이 줄여 잘림 방지 (모바일=180×230, PC=240×230).
            지도 상호작용(팬/줌/마커)이 카드 navigate 를 트리거하지 않도록 stopPropagation. */}
        <div
          ref={mapContainerRef}
          onClick={onClick ? (e) => e.stopPropagation() : undefined}
          onPointerDown={onClick ? (e) => e.stopPropagation() : undefined}
          className="flex-shrink-0 rounded-lg overflow-hidden w-[180px] h-[230px] sm:w-[240px]"
        />
        {/* 오른쪽: 마케터별 — 한 사람당 2 줄 (이름+오늘 / 내일) */}
        {allNames.length > 0 && (
          <div className="flex-1 flex flex-col gap-1 min-w-0 overflow-y-auto">
            {allNames.map(name => {
              const info = marketerInfo[name] || {};
              const todayStr    = info.todayCount    ? `${info.todayCount}건(${info.todayRegion || '-'})`    : '-';
              const tomorrowStr = info.tomorrowCount ? `${info.tomorrowCount}건(${info.tomorrowRegion || '-'})` : '-';
              return (
                <div key={name} className="flex flex-col gap-0 min-w-0 leading-tight">
                  {/* 라인 1: 색점 + 이름 + 오늘 */}
                  <div className="flex items-baseline gap-1 min-w-0">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: nameColor(name) }} />
                    <span className="text-[7px] font-black whitespace-nowrap" style={{ color: nameColor(name) }}>{name}</span>
                    <span className="text-[7px] text-[var(--text)] break-words min-w-0">
                      <span className="text-[var(--text-muted)]">오늘 </span>{todayStr}
                    </span>
                  </div>
                  {/* 라인 2: 내일 (들여쓰기) */}
                  <div className="pl-3 text-[7px] text-[var(--text-sub)] break-words">
                    <span className="text-[var(--text-muted)]">내일 </span>{tomorrowStr}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </UnifiedCard>
  );
}
